import React, { useCallback, useEffect, useMemo } from 'react';
import { Organization } from '../types';
import { useUserOrgs } from '../hooks/useUserOrgs';
import { ApolloError } from '@apollo/client';

interface OrganizationRenderProps {
  organizations: Organization[];
  loading: boolean;
  error: ApolloError | undefined;
  onSelect: (org: Organization) => void;
  onRetry: () => void;
}

interface OrganizationsProps {
  onSelect: (org: Organization) => void;
  children: (props: OrganizationRenderProps) => React.ReactNode;
}

/**
 * Framework-agnostic Organizations component using render props pattern.
 * This component only handles data fetching and state management.
 * The UI rendering is completely controlled by the consumer through render props.
 */
export const Organizations = React.memo(
  ({ onSelect, children }: OrganizationsProps): React.ReactElement => {
    const { loading, organizations, error, fetchOrgs } = useUserOrgs();

    useEffect(() => {
      fetchOrgs();
    }, [fetchOrgs]);

    // Pre-select organization if only one is available
    useEffect(() => {
      if (loading) {
        return;
      }
      if (organizations?.length === 1) {
        onSelect(organizations[0]);
      }
    }, [organizations, loading, onSelect]);

    const handleRetry = useCallback(() => {
      fetchOrgs();
    }, [fetchOrgs]);

    const renderProps = useMemo(
      () => ({
        organizations,
        loading,
        error,
        onSelect,
        onRetry: handleRetry,
      }),
      [organizations, loading, error, onSelect, handleRetry],
    );

    return children(renderProps) as React.ReactElement;
  },
) as React.FC<OrganizationsProps>;
